import { useMemo } from 'react';

import { useQuery } from '@tanstack/react-query';

import { CommonDictionariesSlugs } from '@app/models/dictionaries';
import { SystemDictionarySlugs } from '@app/models/products';
import { VisibilityRuleSchema } from '@app/models/users';

import Api from '@app/services/api';

import { ATTR_META, GEO_SEARCH_MIN_LENGTH } from '../data/constants';
import { AttrSource, RuleAttribute, RuleValue } from '../interfaces';
import { geoNodeLabel, isKnownAttribute } from '../services';

import { useEmployeeTagsQuery } from './useTagsQueries';
export interface Option {
  label: string;
  value: string | number;
}
const DICT_STALE = 5 * 60 * 1000;
export const useRecipientTagsQuery = (enabled = true) =>
  useQuery({
    queryKey: ['dict', 'recipientTags'],
    queryFn: () => Api.dictionaries.getItems(CommonDictionariesSlugs.RecipientTags),
    enabled,
  });

export const useRetailersQuery = (enabled = true) =>
  useQuery({
    queryKey: ['dict', 'retailers'],
    queryFn: () => Api.dictionaries.getItems(CommonDictionariesSlugs.Retailers),
    enabled,
  });

export const useProductTagsQuery = (enabled = true) =>
  useQuery({
    queryKey: ['dict', 'productTags'],
    queryFn: () => Api.products.getDictionaryList(SystemDictionarySlugs.Tags),
    enabled,
  });

export const useProductBrandsQuery = (enabled = true) =>
  useQuery({
    queryKey: ['dict', 'productBrands'],
    queryFn: () => Api.products.getDictionaryList(SystemDictionarySlugs.Brands),
    enabled,
  });

const GEO_SUGGEST_STALE = 30 * 1000;

export const useGeoRegionSuggestQuery = (query: string) => {
  const q = query.trim();

  return useQuery({
    queryKey: ['dict', 'geoSuggest', 'regions', q],
    queryFn: () => Api.dictionaries.getGeoRegionSuggestions({ q }),
    enabled: q.length >= GEO_SEARCH_MIN_LENGTH,
    staleTime: GEO_SUGGEST_STALE,
  });
};

export const useGeoLocalitySuggestQuery = (regionFiasId: string | undefined, query: string) => {
  const q = query.trim();

  return useQuery({
    queryKey: ['dict', 'geoSuggest', 'localities', regionFiasId, q],
    queryFn: () => Api.dictionaries.getGeoLocalitySuggestions({ region_fias_id: regionFiasId as string, q }),
    enabled: !!regionFiasId && q.length >= GEO_SEARCH_MIN_LENGTH,
    staleTime: GEO_SUGGEST_STALE,
  });
};

export const useGeoNodesQuery = (ids: string[]) => {
  const key = useMemo(() => Array.from(new Set(ids)).sort(), [ids]);

  return useQuery({
    queryKey: ['dict', 'geoNodes', key],
    queryFn: () => Api.dictionaries.getGeoNodes({ ids: key.join(',') }),
    enabled: key.length > 0,
    staleTime: DICT_STALE,
  });
};

export const useGeoLabels = (codes: RuleValue[]): { labelOf: (v: RuleValue) => string; isLoading: boolean } => {
  const ids = useMemo(() => codes.map(String), [codes]);
  const nodes = useGeoNodesQuery(ids);
  const labels = useMemo(() => {
    const result: Record<string, string> = {};
    (nodes.data || []).forEach((node) => {
      result[node.fias_id] = geoNodeLabel(node);
    });
    return result;
  }, [nodes.data]);

  return {
    labelOf: (v: RuleValue) => labels[String(v)] || String(v),
    isLoading: nodes.isInitialLoading,
  };
};

const geoCodesOf = (rule?: VisibilityRuleSchema): RuleValue[] =>
  (rule?.conditions || [])
    .filter((cond) => cond.attribute === RuleAttribute.RecipientGeo)
    .flatMap((cond) => [...(cond.include || []), ...(cond.exclude || [])])
    .map(String);

/**
 * Опции и резолвер названий для НЕгео-источника.
 * recipientTag/retailer/authorTag — строковый uid; productTag/productBrand — числовой id.
 */
export const useSourceOptions = (
  source: Exclude<AttrSource, AttrSource.Geo>
): { options: Option[]; isLoading: boolean; labelOf: (v: RuleValue) => string } => {
  const recipientTags = useRecipientTagsQuery();
  const retailers = useRetailersQuery();
  const authorTags = useEmployeeTagsQuery();
  const productTags = useProductTagsQuery();
  const productBrands = useProductBrandsQuery();
  const { options, isLoading } = useMemo(() => {
    switch (source) {
      case AttrSource.RecipientTag:
        return {
          options: (recipientTags.data || []).map((i) => ({ label: i.title, value: i.uid })),
          isLoading: recipientTags.isLoading,
        };
      case AttrSource.Retailer:
        return {
          options: (retailers.data || []).map((i) => ({ label: i.title, value: i.uid })),
          isLoading: retailers.isLoading,
        };
      case AttrSource.AuthorTag:
        return {
          options: (authorTags.data || []).map((i) => ({ label: i.title, value: i.uid })),
          isLoading: authorTags.isLoading,
        };
      case AttrSource.ProductTag:
        return {
          options: (productTags.data || []).map((i) => ({ label: i.title, value: i.id })),
          isLoading: productTags.isLoading,
        };
      case AttrSource.ProductBrand:
        return {
          options: (productBrands.data || []).map((i) => ({ label: i.title, value: i.id })),
          isLoading: productBrands.isLoading,
        };
      default:
        return { options: [] as Option[], isLoading: false };
    }
  }, [
    source,
    recipientTags.data,
    recipientTags.isLoading,
    retailers.data,
    retailers.isLoading,
    authorTags.data,
    authorTags.isLoading,
    productTags.data,
    productTags.isLoading,
    productBrands.data,
    productBrands.isLoading,
  ]);
  const map = useMemo(() => {
    const m = new Map<string, string>();
    options.forEach((o) => m.set(String(o.value), o.label));
    return m;
  }, [options]);
  const labelOf = (v: RuleValue) => map.get(String(v)) || String(v);
  return { options, isLoading, labelOf };
};
/**
 * Резолвер названий для чипсов правила (карточка-обзор).
 * Негео значения резолвятся по словарям; гео — по получателям пространства и внешнему справочнику.
 */
export const useRuleValueLabeler = (rule?: VisibilityRuleSchema) => {
  const sources = useMemo(
    () =>
      new Set(
        (rule?.conditions || [])
          .map((cond) => cond.attribute)
          .filter(isKnownAttribute)
          .map((attribute) => ATTR_META[attribute].source)
      ),
    [rule]
  );
  const recipientTags = useRecipientTagsQuery(sources.has(AttrSource.RecipientTag));
  const retailers = useRetailersQuery(sources.has(AttrSource.Retailer));
  const authorTags = useEmployeeTagsQuery(sources.has(AttrSource.AuthorTag));
  const productTags = useProductTagsQuery(sources.has(AttrSource.ProductTag));
  const productBrands = useProductBrandsQuery(sources.has(AttrSource.ProductBrand));
  const geoCodes = useMemo(() => geoCodesOf(rule), [rule]);
  const geo = useGeoLabels(geoCodes);
  const maps = useMemo(() => {
    const build = (arr: { key: string; title: string }[]) => {
      const m = new Map<string, string>();
      arr.forEach((i) => m.set(i.key, i.title));
      return m;
    };
    return {
      [AttrSource.RecipientTag]: build((recipientTags.data || []).map((i) => ({ key: String(i.uid), title: i.title }))),
      [AttrSource.Retailer]: build((retailers.data || []).map((i) => ({ key: String(i.uid), title: i.title }))),
      [AttrSource.AuthorTag]: build((authorTags.data || []).map((i) => ({ key: String(i.uid), title: i.title }))),
      [AttrSource.ProductTag]: build((productTags.data || []).map((i) => ({ key: String(i.id), title: i.title }))),
      [AttrSource.ProductBrand]: build((productBrands.data || []).map((i) => ({ key: String(i.id), title: i.title }))),
    };
  }, [recipientTags.data, retailers.data, authorTags.data, productTags.data, productBrands.data]);
  /** Название значения по атрибуту (fallback — сам идентификатор). */
  const labelOf = (attribute: string, value: RuleValue): string => {
    if (!isKnownAttribute(attribute)) {
      return String(value);
    }
    const source = ATTR_META[attribute].source;
    if (source === AttrSource.Geo) {
      return geo.labelOf(value);
    }
    return maps[source].get(String(value)) || String(value);
  };
  const isLoading =
    recipientTags.isInitialLoading ||
    retailers.isInitialLoading ||
    authorTags.isInitialLoading ||
    productTags.isInitialLoading ||
    productBrands.isInitialLoading ||
    geo.isLoading;
  return { labelOf, isLoading };
};
